import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import PoseEditor from '../components/PoseEditor';
import axios from 'axios';
import { 
  Upload, Download, Play, RefreshCw, Check, AlertCircle, Loader2,
  Box, Wand2, Film, ChevronRight, Plus, Trash2, Eye, RotateCcw, ZoomIn, Move
} from 'lucide-react';

// 3D Model Viewer Component using CDN
const ModelViewer = ({ projectId, alt, height = '400px' }) => {
  const containerRef = useRef(null);
  
  useEffect(() => {
    // Load model-viewer script from CDN
    if (!document.querySelector('script[src*="model-viewer"]')) {
      const script = document.createElement('script');
      script.type = 'module';
      script.src = 'https://ajax.googleapis.com/ajax/libs/model-viewer/3.3.0/model-viewer.min.js';
      document.head.appendChild(script);
    }
  }, []);
  
  if (!projectId) {
    return (
      <div className="flex items-center justify-center bg-slate-900 rounded-lg" style={{ height }}>
        <p className="text-slate-400">No 3D model available</p>
      </div>
    );
  }
  
  // Use proxy URL to bypass CORS
  const proxyUrl = `${API}/api/3d-lab/proxy-model/${projectId}?format=glb`;
  
  return (
    <div 
      ref={containerRef}
      className="relative bg-slate-900 rounded-lg overflow-hidden" 
      style={{ height }}
      dangerouslySetInnerHTML={{
        __html: `
          <model-viewer
            src="${proxyUrl}"
            alt="${alt || '3D Model'}"
            camera-controls
            auto-rotate
            shadow-intensity="1"
            environment-image="neutral"
            exposure="1"
            style="width: 100%; height: 100%;"
          ></model-viewer>
        `
      }}
    />
  );
};

const API = process.env.REACT_APP_BACKEND_URL;

// Status badges config
const STATUS_CONFIG = {
  created: { label: 'Ready', color: 'bg-gray-500' },
  converting: { label: 'Converting to 3D', color: 'bg-blue-500' },
  model_ready: { label: 'Model Ready', color: 'bg-green-500' },
  rigging: { label: 'Rigging', color: 'bg-purple-500' },
  rigged: { label: 'Rigged', color: 'bg-emerald-500' },
  conversion_failed: { label: 'Failed', color: 'bg-red-500' },
  rig_failed: { label: 'Model Ready', color: 'bg-yellow-500' },
};

export default function Lab3D() {
  // Project state
  const [projectId, setProjectId] = useState(null);
  const [projectName, setProjectName] = useState('');
  const [project, setProject] = useState(null);
  const [projects, setProjects] = useState([]);
  
  // Animation state
  const [presets, setPresets] = useState({});
  const [selectedPreset, setSelectedPreset] = useState(null);
  
  // UI state
  const [mode, setMode] = useState('select'); // select, upload, model, animate, pose
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pollInterval, setPollInterval] = useState(null);
  const [showPoseEditor, setShowPoseEditor] = useState(false);

  // Load projects and presets on mount
  useEffect(() => {
    loadProjects();
    loadPresets();
    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, []);

  // Poll for status updates when converting
  useEffect(() => {
    if (project?.status === 'converting' || project?.status === 'rigging') {
      const interval = setInterval(() => {
        refreshProjectStatus();
      }, 3000);
      setPollInterval(interval);
      return () => clearInterval(interval);
    } else if (pollInterval) {
      clearInterval(pollInterval);
      setPollInterval(null);
    }
  }, [project?.status]);

  // Also poll when animations are rendering
  useEffect(() => {
    const hasRenderingAnim = project?.animations && 
      Object.values(project.animations).some(a => a.status === 'rendering');
    
    if (hasRenderingAnim && !pollInterval) {
      const interval = setInterval(() => {
        refreshProjectStatus();
      }, 2000);
      setPollInterval(interval);
      return () => clearInterval(interval);
    }
  }, [project?.animations]);

  const loadProjects = async () => {
    try {
      const resp = await axios.get(`${API}/api/3d-lab/projects`);
      setProjects(resp.data.projects || []);
    } catch (err) {
      console.error('Failed to load projects:', err);
    }
  };

  const loadPresets = async () => {
    try {
      const resp = await axios.get(`${API}/api/3d-lab/animation-presets`);
      setPresets(resp.data.presets || {});
    } catch (err) {
      console.error('Failed to load presets:', err);
    }
  };

  const selectProject = async (proj) => {
    setLoading(true);
    setProjectId(proj.id);
    setProjectName(proj.name);
    
    try {
      const resp = await axios.get(`${API}/api/3d-lab/project/${proj.id}/status`);
      setProject(resp.data);
      
      // Set mode based on status
      if (resp.data.status === 'rigged' || resp.data.status === 'styled' || resp.data.status === 'retexturing') {
        setMode('animate');
      } else if (resp.data.status === 'model_ready' || resp.data.status === 'rig_failed') {
        // Show model mode for both ready and failed rig (so user can download/retry)
        setMode('model');
      } else if (resp.data.status === 'created') {
        setMode('upload');
      } else if (resp.data.status === 'converting' || resp.data.status === 'rigging') {
        setMode('upload');
      } else {
        setMode('upload');
      }
    } catch (err) {
      setError('Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const deleteProject = async (proj, e) => {
    e.stopPropagation(); // Prevent selecting the project
    
    if (!window.confirm(`Are you sure you want to delete "${proj.name}"? This cannot be undone.`)) {
      return;
    }
    
    setLoading(true);
    try {
      await axios.delete(`${API}/api/3d-lab/project/${proj.id}?hard_delete=true`);
      loadProjects(); // Refresh the list
      // If we deleted the currently selected project, reset
      if (projectId === proj.id) {
        setProject(null);
        setProjectId(null);
        setMode('select');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete project');
    } finally {
      setLoading(false);
    }
  };

  const refreshProjectStatus = async () => {
    if (!projectId) return;
    try {
      const resp = await axios.get(`${API}/api/3d-lab/project/${projectId}/status`);
      setProject(resp.data);
      
      // Update mode if status changed
      if (resp.data.status === 'rigged' || resp.data.status === 'styled' || resp.data.status === 'retexturing') {
        setMode('animate');
      } else if (resp.data.status === 'model_ready' || resp.data.status === 'rig_failed') {
        setMode('model');
      }
    } catch (err) {
      console.error('Failed to refresh status:', err);
    }
  };

  const createProject = async () => {
    if (!projectName.trim()) {
      setError('Please enter a project name');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('name', projectName);
      formData.append('description', '');
      
      const resp = await axios.post(`${API}/api/3d-lab/project`, formData);
      setProjectId(resp.data.project_id);
      setProject({
        project_id: resp.data.project_id,
        name: projectName,
        status: 'created',
        progress: 0,
        model_urls: {},
        animations: {}
      });
      setMode('upload');
      loadProjects();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  const uploadImage = async (file) => {
    if (!projectId || !file) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(`${API}/api/3d-lab/project/${projectId}/upload-image`, formData);
      
      // Update local state
      setProject(prev => ({ ...prev, status: 'converting', progress: 0 }));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setLoading(false);
    }
  };

  const startMeshyRig = async () => {
    if (!projectId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      await axios.post(`${API}/api/3d-lab/project/${projectId}/meshy-rig`);
      setProject(prev => ({ ...prev, status: 'rigging' }));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start Meshy AI rigging');
    } finally {
      setLoading(false);
    }
  };

  const startBlenderRig = async () => {
    if (!projectId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      await axios.post(`${API}/api/3d-lab/project/${projectId}/blender-rig`);
      setProject(prev => ({ ...prev, status: 'rigging' }));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start rigging');
    } finally {
      setLoading(false);
    }
  };

  const createAnimation = async (presetId) => {
    if (!projectId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('preset_id', presetId);
      
      const resp = await axios.post(`${API}/api/3d-lab/project/${projectId}/animate`, formData);
      
      // Refresh to get updated animations
      setTimeout(refreshProjectStatus, 1000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create animation');
    } finally {
      setLoading(false);
    }
  };

  const resetProject = () => {
    setProjectId(null);
    setProjectName('');
    setProject(null);
    setMode('select');
    setError(null);
    loadProjects();
  };

  const renderStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || { label: status, color: 'bg-gray-500' };
    return (
      <Badge className={`${config.color} text-white`}>
        {config.label}
      </Badge>
    );
  };

  // Project Selection View
  const renderSelectMode = () => (
    <div className="space-y-6">
      {/* Create New Project */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Plus className="w-5 h-5 text-cyan-400" />
            Create New 3D Character
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-3">
            <Input
              placeholder="Character name..."
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              className="bg-slate-700 border-slate-600"
              onKeyDown={(e) => e.key === 'Enter' && createProject()}
              data-testid="3d-lab-character-name-input"
            />
            <Button 
              onClick={createProject} 
              disabled={loading || !projectName.trim()}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid="3d-lab-create-button"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Existing Projects */}
      {projects.length > 0 && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-lg">Your 3D Projects</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3">
              {projects.map((proj) => (
                <div 
                  key={proj.id}
                  onClick={() => selectProject(proj)}
                  className="flex items-center gap-4 p-4 bg-slate-700/50 rounded-lg cursor-pointer hover:bg-slate-700 transition-colors group"
                  data-testid={`3d-lab-project-item-${proj.id}`}
                >
                  <div className="w-16 h-16 bg-slate-600 rounded-lg flex items-center justify-center overflow-hidden">
                    {proj.thumbnail_url ? (
                      <img src={proj.thumbnail_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Box className="w-8 h-8 text-slate-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-white">{proj.name}</h3>
                    <p className="text-sm text-slate-400">
                      Created: {new Date(proj.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  {renderStatusBadge(proj.status)}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => deleteProject(proj, e)}
                    className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 hover:bg-red-900/30 transition-all"
                    data-testid={`delete-3d-project-${proj.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // Upload View
  const renderUploadMode = () => (
    <div className="space-y-6">
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Upload className="w-5 h-5 text-cyan-400" />
            Upload Character Image
          </CardTitle>
          <CardDescription>
            Upload a clear image of your character. Meshy AI will convert it to a 3D model.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {project?.source_image_url ? (
            <div className="relative">
              <img 
                src={project.source_image_url} 
                alt="Source" 
                className="w-full max-h-64 object-contain rounded-lg bg-slate-700"
              />
              {project.status === 'converting' && (
                <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center rounded-lg">
                  <Loader2 className="w-8 h-8 animate-spin text-cyan-400 mb-2" />
                  <span className="text-white">Converting to 3D...</span>
                  <Progress value={project.progress || 0} className="w-48 mt-2" />
                  <span className="text-sm text-slate-300 mt-1">{project.progress || 0}%</span>
                </div>
              )}
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-slate-600 rounded-lg cursor-pointer hover:border-cyan-500 transition-colors">
              <Upload className="w-10 h-10 text-slate-400 mb-2" />
              <span className="text-slate-400">Click to upload character image</span>
              <span className="text-sm text-slate-500">PNG or JPG recommended</span>
              <input
                type="file"
                accept="image/png,image/jpeg"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])}
                disabled={loading}
              />
            </label>
          )}

          {project?.status === 'conversion_failed' && (
            <div className="flex items-center gap-2 text-red-400 bg-red-900/20 p-3 rounded-lg">
              <AlertCircle className="w-5 h-5" />
              <span>{project.error || 'Conversion failed'}</span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  // Model View
  const renderModelMode = () => (
    <div className="space-y-6">
      {/* Interactive 3D Model Viewer */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Box className="w-5 h-5 text-green-400" />
            3D Model Viewer
          </CardTitle>
          <CardDescription>
            Drag to rotate • Scroll to zoom • Shift+drag to pan
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ModelViewer 
            projectId={projectId} 
            alt={project?.name}
            height="400px"
          />
        </CardContent>
      </Card>

      {/* Download Links */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-lg">Download Model</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {project?.model_urls?.glb && (
              <a 
                href={project.model_urls.glb} 
                download
                className="flex items-center justify-center gap-2 p-3 bg-cyan-700 rounded-lg hover:bg-cyan-600 transition-colors text-white"
              >
                <Download className="w-4 h-4" />
                <span>GLB</span>
              </a>
            )}
            {project?.model_urls?.fbx && (
              <a 
                href={project.model_urls.fbx} 
                download
                className="flex items-center justify-center gap-2 p-3 bg-slate-700 rounded-lg hover:bg-slate-600 transition-colors"
              >
                <Download className="w-4 h-4" />
                <span>FBX</span>
              </a>
            )}
            {project?.model_urls?.obj && (
              <a 
                href={project.model_urls.obj} 
                download
                className="flex items-center justify-center gap-2 p-3 bg-slate-700 rounded-lg hover:bg-slate-600 transition-colors"
              >
                <Download className="w-4 h-4" />
                <span>OBJ</span>
              </a>
            )}
            {project?.model_urls?.usdz && (
              <a 
                href={project.model_urls.usdz} 
                download
                className="flex items-center justify-center gap-2 p-3 bg-slate-700 rounded-lg hover:bg-slate-600 transition-colors"
              >
                <Download className="w-4 h-4" />
                <span>USDZ</span>
              </a>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Rigging Options */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-lg">Auto-Rig for Animation</CardTitle>
          <CardDescription>
            Choose AI-powered rigging for best results with proper joint detection
          </CardDescription>
        </CardHeader>
        <CardContent>
          {project?.status === 'rigging' ? (
            <div className="space-y-3 p-4 bg-purple-900/30 rounded-lg">
              <div className="flex items-center gap-3">
                <Loader2 className="w-5 h-5 animate-spin text-purple-400" />
                <span>Meshy AI rigging in progress...</span>
              </div>
              <Progress value={project?.rig_progress || 0} className="h-2" />
              <span className="text-sm text-slate-400">{project?.rig_progress || 0}% complete</span>
            </div>
          ) : (
            <div className="space-y-3">
              <Button 
                onClick={startMeshyRig}
                disabled={loading}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                data-testid="meshy-rig-button"
              >
                <Wand2 className="w-4 h-4 mr-2" />
                Auto-Rig with Meshy AI (Recommended)
              </Button>
              <p className="text-xs text-slate-400 text-center">
                Includes walking & running animations
              </p>
            </div>
          )}

          {project?.status === 'rig_failed' && (
            <div className="bg-red-900/20 border border-red-700/50 p-4 rounded-lg mt-3">
              <div className="flex items-center gap-2 text-red-400 mb-2">
                <AlertCircle className="w-5 h-5" />
                <span className="font-medium">Rigging failed</span>
              </div>
              <p className="text-sm text-slate-300 mb-2">
                {project?.error || 'Unknown error occurred'}
              </p>
              <p className="text-sm text-slate-400">
                Try again with Meshy AI, or use Mixamo for manual rigging.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  // Animation View
  const renderAnimateMode = () => (
    <div className="space-y-6">
      {/* 3D Model Viewer for rigged model */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Check className="w-5 h-5 text-emerald-400" />
            Rigged Model Preview
          </CardTitle>
          <CardDescription>
            Drag to rotate • Scroll to zoom • Your model is ready for animation
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ModelViewer 
            projectId={projectId} 
            alt={project?.name}
            height="300px"
          />
          <div className="flex flex-wrap items-center gap-3 mt-3">
            {project?.rigged_model_urls?.glb && (
              <a 
                href={project.rigged_model_urls.glb} 
                download
                className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300"
              >
                <Download className="w-4 h-4" />
                Rigged GLB
              </a>
            )}
            {project?.rigged_model_urls?.fbx && (
              <a 
                href={project.rigged_model_urls.fbx} 
                download
                className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300"
              >
                <Download className="w-4 h-4" />
                Rigged FBX
              </a>
            )}
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowPoseEditor(true)}
              className="border-purple-500 text-purple-400 hover:bg-purple-900/20"
            >
              <Move className="w-4 h-4 mr-1" />
              Pose Editor
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Meshy Included Animations */}
      {project?.meshy_animations && Object.keys(project.meshy_animations).length > 0 && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Film className="w-5 h-5 text-green-400" />
              Included Animations (from Meshy)
            </CardTitle>
            <CardDescription>
              These animations came with your rigged model - download directly!
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {project.meshy_animations.walking_glb_url && (
                <a 
                  href={project.meshy_animations.walking_glb_url}
                  download
                  className="flex items-center justify-center gap-2 p-3 bg-green-700/30 border border-green-600 rounded-lg hover:bg-green-700/50 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  <span>Walking (GLB)</span>
                </a>
              )}
              {project.meshy_animations.walking_fbx_url && (
                <a 
                  href={project.meshy_animations.walking_fbx_url}
                  download
                  className="flex items-center justify-center gap-2 p-3 bg-slate-700/50 rounded-lg hover:bg-slate-600 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  <span>Walking (FBX)</span>
                </a>
              )}
              {project.meshy_animations.running_glb_url && (
                <a 
                  href={project.meshy_animations.running_glb_url}
                  download
                  className="flex items-center justify-center gap-2 p-3 bg-green-700/30 border border-green-600 rounded-lg hover:bg-green-700/50 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  <span>Running (GLB)</span>
                </a>
              )}
              {project.meshy_animations.running_fbx_url && (
                <a 
                  href={project.meshy_animations.running_fbx_url}
                  download
                  className="flex items-center justify-center gap-2 p-3 bg-slate-700/50 rounded-lg hover:bg-slate-600 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  <span>Running (FBX)</span>
                </a>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Animation Studio Link */}
      <Card className="bg-gradient-to-r from-purple-900/50 to-pink-900/50 border-purple-500/50">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-white">Ready to Animate?</h4>
              <p className="text-sm text-slate-300">Open Animation Studio to create videos with timeline, backgrounds, and more animations</p>
            </div>
            <a 
              href="/animation-studio"
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white font-medium transition-colors"
              data-testid="open-animation-studio"
            >
              <Film className="w-4 h-4" />
              Open Animation Studio
            </a>
          </div>
        </CardContent>
      </Card>

      {/* Pose Editor Modal */}
      {showPoseEditor && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-lg flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Move className="w-5 h-5 text-purple-400" />
                Pose Editor
              </span>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowPoseEditor(false)}
              >
                Close
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <PoseEditor 
              projectId={projectId}
              riggedModelUrl={project?.rigged_model_url}
              onSavePose={(pose) => {
                console.log('Saved pose:', pose);
                // TODO: Save pose to backend
              }}
            />
          </CardContent>
        </Card>
      )}

      {/* Animation Presets */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Film className="w-5 h-5 text-cyan-400" />
            Create Animation
          </CardTitle>
          <CardDescription>
            Select an animation preset to render. Each takes 2-4 minutes.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {Object.entries(presets).map(([id, preset]) => (
              <Button
                key={id}
                variant="outline"
                onClick={() => createAnimation(id)}
                disabled={loading}
                className="h-auto py-3 flex flex-col items-center gap-1 border-slate-600 hover:border-cyan-500 hover:bg-cyan-900/20"
              >
                <Play className="w-5 h-5" />
                <span>{preset.name}</span>
                <span className="text-xs text-slate-400">{preset.frames} frames</span>
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Generated Animations */}
      {project?.animations && Object.keys(project.animations).length > 0 && (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-lg">Generated Animations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(project.animations).map(([animId, anim]) => (
                <div 
                  key={animId}
                  className="flex items-center gap-4 p-3 bg-slate-700/50 rounded-lg"
                >
                  <div className="flex-1">
                    <h4 className="font-medium">{anim.preset_name}</h4>
                    <p className="text-sm text-slate-400">
                      {new Date(anim.created_at).toLocaleString()}
                    </p>
                  </div>
                  
                  {anim.status === 'rendering' ? (
                    <div className="flex items-center gap-3">
                      <div className="w-32">
                        <Progress value={anim.progress || 0} className="h-2" />
                      </div>
                      <span className="text-sm text-cyan-400 min-w-[3rem]">
                        {anim.progress || 0}%
                      </span>
                      <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
                    </div>
                  ) : anim.status === 'complete' ? (
                    <div className="flex gap-2">
                      <a 
                        href={anim.video_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="p-2 bg-slate-600 rounded hover:bg-slate-500"
                      >
                        <Eye className="w-4 h-4" />
                      </a>
                      <a 
                        href={anim.video_url} 
                        download
                        className="p-2 bg-cyan-600 rounded hover:bg-cyan-500"
                      >
                        <Download className="w-4 h-4" />
                      </a>
                    </div>
                  ) : (
                    <Badge className="bg-red-500">Failed</Badge>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      {/* Header */}
      <div className="border-b border-slate-700 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Box className="w-8 h-8 text-cyan-400" />
              <div>
                <h1 className="text-xl font-bold">3D Lab</h1>
                <p className="text-sm text-slate-400">Convert 2D characters to animated 3D models</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {project && (
                <>
                  <span className="text-slate-300">{project.name}</span>
                  {renderStatusBadge(project.status)}
                </>
              )}
              {projectId && (
                <Button variant="outline" size="sm" onClick={resetProject} className="border-slate-600" data-testid="3d-lab-new-project-button">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  New Project
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {error && (
          <div className="mb-6 flex items-center gap-2 text-red-400 bg-red-900/20 p-4 rounded-lg">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
            <button onClick={() => setError(null)} className="ml-auto text-red-300 hover:text-white">×</button>
          </div>
        )}

        {/* Workflow Progress */}
        {projectId && (
          <div className="mb-8">
            <div className="flex items-center justify-between text-sm">
              <span className={mode === 'upload' ? 'text-cyan-400 font-medium' : 'text-slate-500'}>
                1. Upload Image
              </span>
              <span className={mode === 'model' ? 'text-cyan-400 font-medium' : 'text-slate-500'}>
                2. 3D Model
              </span>
              <span className={mode === 'animate' ? 'text-cyan-400 font-medium' : 'text-slate-500'}>
                3. Animate
              </span>
            </div>
            <div className="mt-2 h-1 bg-slate-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-cyan-500 transition-all duration-500"
                style={{ 
                  width: mode === 'upload' ? '33%' : mode === 'model' ? '66%' : '100%' 
                }}
              />
            </div>
          </div>
        )}

        {/* Mode-specific content */}
        {mode === 'select' && renderSelectMode()}
        {mode === 'upload' && renderUploadMode()}
        {mode === 'model' && renderModelMode()}
        {mode === 'animate' && renderAnimateMode()}
      </div>
    </div>
  );
}
